import React, { useState } from 'react';
import { GripVertical, Edit, Trash2, CheckCircle2, Circle, Brain, Lightbulb } from 'lucide-react';
import { DifficultyBadge, DifficultyLevel } from './DifficultyBadge';
import DeleteConfirmationModal from '@/components/ui/DeleteConfirmationModal';

export type QuestionType = 'SINGLE_CHOICE' | 'MULTIPLE_CHOICE' | 'TRUE_FALSE' | 'SHORT_ANSWER' | 'LONG_ANSWER';

interface QuestionOption {
  id: string;
  text: string;
  is_correct: boolean;
}

interface ExamQuestion {
  id: string;
  question_text: string;
  question_type: QuestionType;
  options?: QuestionOption[];
  correct_answer?: string;
  answer_keywords?: string[];
  points: number;
  difficulty_level: DifficultyLevel;
  bloom_level?: string;
  topic?: string;
  course_id?: string;
  explanation: string;
  hint?: string;
  source: 'AI_GENERATED' | 'MANUAL' | 'IMPORTED';
  order: number;
}

interface QuestionCardProps {
  question: ExamQuestion;
  index: number;
  isDragging?: boolean;
  onEdit: (question: ExamQuestion) => void;
  onDelete: (questionId: string) => void;
  dragHandleProps?: any;
  className?: string;
}

const questionTypeLabels: Record<QuestionType, string> = {
  SINGLE_CHOICE: 'Single Choice',
  MULTIPLE_CHOICE: 'Multiple Choice',
  TRUE_FALSE: 'True/False',
  SHORT_ANSWER: 'Short Answer',
  LONG_ANSWER: 'Long Answer',
};

const questionTypeColors: Record<QuestionType, string> = {
  SINGLE_CHOICE: 'bg-blue-100 text-blue-700',
  MULTIPLE_CHOICE: 'bg-purple-100 text-purple-700',
  TRUE_FALSE: 'bg-green-100 text-green-700',
  SHORT_ANSWER: 'bg-orange-100 text-orange-700',
  LONG_ANSWER: 'bg-red-100 text-red-700',
};

const sourceColors = {
  AI_GENERATED: 'bg-purple-100 text-purple-700',
  MANUAL: 'bg-gray-100 text-gray-700',
  IMPORTED: 'bg-blue-100 text-blue-700',
};

export const QuestionCard: React.FC<QuestionCardProps> = ({
  question,
  index,
  isDragging = false,
  onEdit,
  onDelete,
  dragHandleProps,
  className = '',
}) => {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const handleDelete = () => {
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    onDelete(question.id);
    setIsDeleteModalOpen(false);
  };

  const renderOptions = () => {
    if (!question.options || question.options.length === 0) return null;

    return (
      <div className="space-y-2">
        {question.options.map((option, idx) => (
          <div
            key={option.id}
            className={`
              flex items-start gap-3 p-3 rounded-lg border
              ${
                option.is_correct
                  ? 'bg-green-50 border-green-200'
                  : 'bg-gray-50 border-gray-200'
              }
            `}
          >
            {option.is_correct ? (
              <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            ) : (
              <Circle className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
            )}
            <div className="flex-1">
              <span
                className={`text-sm ${
                  option.is_correct ? 'text-green-900 font-medium' : 'text-gray-700'
                }`}
              >
                {String.fromCharCode(65 + idx)}. {option.text}
              </span>
              {option.is_correct && (
                <span className="ml-2 text-xs text-green-600 font-semibold">✓ Correct</span>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderTextAnswer = () => {
    if (question.question_type === 'SHORT_ANSWER' || question.question_type === 'LONG_ANSWER') {
      return (
        <div className="space-y-2">
          {question.correct_answer && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-xs font-medium text-green-700 mb-1">Expected Answer:</p>
              <p className="text-sm text-green-900">{question.correct_answer}</p>
            </div>
          )}
          {question.answer_keywords && question.answer_keywords.length > 0 && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-xs font-medium text-blue-700 mb-2">Keywords:</p>
              <div className="flex flex-wrap gap-2">
                {question.answer_keywords.map((keyword, idx) => (
                  <span
                    key={idx}
                    className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded"
                  >
                    {keyword}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div
      className={`
        bg-white rounded-lg border border-gray-200 shadow-sm
        hover:shadow-md transition-all duration-200
        ${isDragging ? 'opacity-50 rotate-2 scale-105' : ''}
        ${className}
      `}
    >
      {/* Header */}
      <div className="flex items-start gap-3 p-4 border-b border-gray-100">
        {/* Drag Handle */}
        <div
          {...dragHandleProps}
          className="cursor-move text-gray-400 hover:text-gray-600 transition-colors mt-1"
        >
          <GripVertical className="w-5 h-5" />
        </div>

        {/* Question Number */}
        <div className="flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-700 font-bold text-sm rounded-lg flex-shrink-0">
          {index + 1}
        </div>

        {/* Question Text */}
        <div className="flex-1 min-w-0">
          <p className="text-base font-medium text-gray-900 mb-2 leading-relaxed">
            {question.question_text}
          </p>

          {/* Badges */}
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`px-2.5 py-1 text-xs font-medium rounded-full ${
                questionTypeColors[question.question_type]
              }`}
            >
              {questionTypeLabels[question.question_type]}
            </span>

            <DifficultyBadge difficulty={question.difficulty_level} size="sm" />

            <span
              className={`px-2.5 py-1 text-xs font-medium rounded-full ${
                sourceColors[question.source]
              }`}
            >
              {question.source.replace('_', ' ')}
            </span>

            <span className="px-2.5 py-1 text-xs font-medium bg-indigo-100 text-indigo-700 rounded-full">
              {question.points} {question.points === 1 ? 'point' : 'points'}
            </span>

            {question.topic && (
              <span className="px-2.5 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-full">
                {question.topic}
              </span>
            )}

            {question.bloom_level && (
              <span className="px-2.5 py-1 text-xs font-medium bg-teal-100 text-teal-700 rounded-full flex items-center gap-1">
                <Brain className="w-3 h-3" />
                {question.bloom_level}
              </span>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => onEdit(question)}
            className="
              p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50
              rounded-lg transition-colors
              focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
            "
            title="Edit Question"
          >
            <Edit className="w-4 h-4" />
          </button>

          <button
            onClick={handleDelete}
            className="
              p-2 text-gray-600 hover:text-red-600 hover:bg-red-50
              rounded-lg transition-colors
              focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2
            "
            title="Delete Question"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="p-4 space-y-4">
        {/* Options or Text Answer */}
        {renderOptions()}
        {renderTextAnswer()}

        {/* Explanation */}
        {question.explanation && (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start gap-2">
              <Lightbulb className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-xs font-medium text-amber-700 mb-1">Explanation:</p>
                <p className="text-sm text-amber-900">{question.explanation}</p>
              </div>
            </div>
          </div>
        )}

        {/* Hint */}
        {question.hint && (
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start gap-2">
              <Lightbulb className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-xs font-medium text-blue-700 mb-1">Hint:</p>
                <p className="text-sm text-blue-900">{question.hint}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Question"
        message="Are you sure you want to delete this question?"
      />
    </div>
  );
};
